import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';

import IntegrationMonitoringService from '@/services-http/servicos/IntegrationMonitoringService';
import store from '@/store/index';
import i18n from '@/plugins/i18n';

import {
  buildFilterParams,
  defaultFilter,
  defaultPagination,
  initState,
  normalizeIndicators,
  normalizeMonitoringPage,
} from './integration-monitoring-factory';
import {
  IntegrationMonitoringFilter,
  IntegrationMonitoringIndicators,
  IntegrationMonitoringModel,
  IntegrationMonitoringRequestItem,
  IntegrationMonitoringTableOptions,
  Modal,
} from './integration-monitoring-types';

let searchRequestSequence = 0;

const extractFilenameFromDisposition = (contentDisposition: string | undefined, batchId: string): string => {
  if (!contentDisposition) {
    return `integration-batch-${batchId}.json`;
  }

  const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = fileNameRegex.exec(contentDisposition);
  if (matches && matches[1]) {
    return decodeURIComponent(matches[1].replace(/['"]/g, ''));
  }

  return `integration-batch-${batchId}.json`;
};

const httpStatusFromError = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;
  const response = (error as { response?: { status?: number } }).response;
  return typeof response?.status === 'number' ? response.status : null;
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const fileURL = window.URL.createObjectURL(blob);
  const fileLink = document.createElement('a');
  fileLink.href = fileURL;
  fileLink.setAttribute('download', filename);
  document.body.appendChild(fileLink);
  fileLink.click();
  document.body.removeChild(fileLink);
  window.URL.revokeObjectURL(fileURL);
};

@Module({
  namespaced: true,
  name: 'integration-monitoring',
  dynamic: true,
  store,
})
class IntegrationMonitoringStore extends VuexModule {
  data: IntegrationMonitoringModel = initState;

  get filter() {
    return this.data.filter;
  }

  get records() {
    return this.data.records;
  }

  get loadingPage() {
    return this.data.loadingPage;
  }

  get loadingTable() {
    return this.data.loadingTable;
  }

  get loadingExport() {
    return this.data.loadingExport;
  }

  get exportingById() {
    return this.data.exportingById;
  }

  get pagination() {
    return this.data.pagination;
  }

  get indicators() {
    return this.data.indicators;
  }

  get showSnackbarCustomize() {
    return this.data.showSnackbarCustomize;
  }

  get hasEntityFilter() {
    const { carrierId, financialGroupId, contractId } = this.data.filter;
    return carrierId != null || financialGroupId != null || contractId != null;
  }

  get hasFiltersSelected() {
    return Boolean(
      this.data.filter.integrationType
      && this.data.filter.processType
      && this.data.filter.periodStart
      && this.data.filter.periodEnd
      && this.hasEntityFilter,
    );
  }

  get canSearch() {
    return this.hasFiltersSelected;
  }

  get hasResults() {
    return this.data.records.length > 0;
  }

  get showTable() {
    return this.data.indicators !== null || this.data.loadingTable;
  }

  @Action
  initPage() {
    this.RESET_FILTER_STATE();
    this.CLEAR_RESULTS();
  }

  @Action
  applyFilter(filter: Partial<IntegrationMonitoringFilter>) {
    this.SET_FILTER(filter);
  }

  @Action
  changeProcessType(processType: string) {
    this.SET_FILTER({ processType, status: [] });
    this.CLEAR_RESULTS();
  }

  @Action
  resetFilter() {
    this.RESET_FILTER_STATE();
    this.CLEAR_RESULTS();
  }

  @Action
  async searchByFilter() {
    if (!this.canSearch) {
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t('integrationMonitoring.messages.requiredFilters')),
        show: true,
      });
      return;
    }

    this.SET_PAGINATION({ page: 1 });
    await this.search();
  }

  @Action
  async updateTableOptions(options: IntegrationMonitoringTableOptions) {
    if (!this.canSearch) return;

    const nextPage = options?.page ?? this.data.pagination.page;
    const nextItemsPerPage = options?.itemsPerPage ?? this.data.pagination.itemsPerPage;
    const hasChanged = nextPage !== this.data.pagination.page
      || nextItemsPerPage !== this.data.pagination.itemsPerPage;

    if (!hasChanged) return;

    this.SET_PAGINATION({ page: nextPage, itemsPerPage: nextItemsPerPage });
    await this.search();
  }

  @Action
  async exportBatch(batchId: string) {
    if (!batchId || this.data.exportingById[batchId]) return;

    this.SET_EXPORTING_BY_ID({ batchId, loading: true });

    try {
      const service = new IntegrationMonitoringService();
      const response = await service.exportBatch(batchId, this.data.filter.processType);
      const blob = response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data ?? {}], { type: 'application/json' });
      const headers = response?.headers ?? {};
      const contentDisposition = headers['content-disposition'] || headers['Content-Disposition'];
      const filename = extractFilenameFromDisposition(contentDisposition, batchId);

      triggerBlobDownload(blob, filename);

      this.showSnackbar({
        type: 'success',
        message: String(i18n.t('integrationMonitoring.messages.exportSuccess')),
        show: true,
      });
    } catch (error) {
      const status = httpStatusFromError(error);
      const messageKey = status === 403
        ? 'integrationMonitoring.messages.forbiddenAccess'
        : 'integrationMonitoring.messages.exportError';
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t(messageKey)),
        show: true,
      });
    } finally {
      this.SET_EXPORTING_BY_ID({ batchId, loading: false });
    }
  }

  @Action
  private async search() {
    searchRequestSequence += 1;
    const currentSearchId = searchRequestSequence;
    const params = buildFilterParams(this.data.filter);
    const { processType } = this.data.filter;
    const pageable = {
      page: this.data.pagination.page - 1,
      size: this.data.pagination.itemsPerPage,
    };

    this.SET_PAGE_LOADING(true);
    this.SET_TABLE_LOADING(true);

    try {
      const service = new IntegrationMonitoringService();
      const [indicatorsResponse, batchesResponse] = await Promise.all([
        service.fetchIndicators(params, processType),
        service.fetchBatches(params, pageable, processType),
      ]);

      if (currentSearchId !== searchRequestSequence) return;

      this.SET_INDICATORS(normalizeIndicators(indicatorsResponse, this.data.filter));
      const page = normalizeMonitoringPage(batchesResponse, processType);
      this.SET_TOTAL_ITEMS(page.totalElements);
      this.SET_RECORDS(page.content);
    } catch (error) {
      if (currentSearchId !== searchRequestSequence) return;

      this.CLEAR_RESULTS();
      const status = httpStatusFromError(error);
      const messageKey = status === 403
        ? 'integrationMonitoring.messages.forbiddenAccess'
        : 'integrationMonitoring.messages.searchError';
      this.showSnackbar({
        type: 'error',
        message: String(i18n.t(messageKey)),
        show: true,
      });
    } finally {
      if (currentSearchId === searchRequestSequence) {
        this.SET_PAGE_LOADING(false);
        this.SET_TABLE_LOADING(false);
      }
    }
  }

  @Mutation
  showSnackbar(modalParams: Modal) {
    this.data.showSnackbarCustomize = modalParams;
    if (modalParams.show) {
      setTimeout(() => {
        this.data.showSnackbarCustomize = { type: null, message: '', show: false };
      }, 5000);
    }
  }

  @Mutation
  private SET_FILTER(filter: Partial<IntegrationMonitoringFilter>) {
    this.data.filter = { ...this.data.filter, ...filter };
  }

  @Mutation
  private RESET_FILTER_STATE() {
    this.data.filter = defaultFilter();
  }

  @Mutation
  private SET_PAGINATION(pagination: Partial<{ page: number; itemsPerPage: number; totalItems: number }>) {
    this.data.pagination = { ...this.data.pagination, ...pagination };
  }

  @Mutation
  private SET_PAGE_LOADING(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  private SET_TABLE_LOADING(loading: boolean) {
    this.data.loadingTable = loading;
  }

  @Mutation
  private SET_EXPORTING_BY_ID({ batchId, loading }: { batchId: string; loading: boolean }) {
    this.data.exportingById = {
      ...this.data.exportingById,
      [batchId]: loading,
    };
    this.data.loadingExport = Object.values(this.data.exportingById).some(Boolean);
  }

  @Mutation
  private SET_INDICATORS(indicators: IntegrationMonitoringIndicators) {
    this.data.indicators = indicators;
  }

  @Mutation
  private SET_TOTAL_ITEMS(total: number) {
    this.data.pagination.totalItems = total;
  }

  @Mutation
  private CLEAR_RECORDS() {
    this.data.records = [];
  }

  @Mutation
  private CLEAR_RESULTS() {
    this.data.records = [];
    this.data.pagination = defaultPagination();
    this.data.indicators = null;
    this.data.exportingById = {};
    this.data.loadingExport = false;
  }

  @Mutation
  private SET_RECORDS(records: IntegrationMonitoringRequestItem[]) {
    this.data.records = records;
  }
}

export default getModule(IntegrationMonitoringStore);
